/**
 * RhFormationsPanel — item 5.20.
 *
 * Le parcours, pas l'échéance : on crée une session, on inscrit des agents
 * (ils sont prévenus), et le jour venu on pointe et on dépose la preuve.
 *
 * L'émargement est ce qui distingue cet écran d'une liste de cases à cocher :
 * une commune contrôlée sur son plan de formation doit produire un document.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiException } from '@/lib/api';
import { Card, Button, Badge, Alert, Field } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import {
  GraduationCap, Plus, ArrowLeft, Loader2, AlertCircle, Camera, Check, X, Info,
  FileText, Lock,
} from 'lucide-react';
import { DemandesFormationRhCard } from './DemandesFormationRhCard';
import { DeplacementsRhCard } from './DeplacementsRhCard';

const CHAMP = 'h-10 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-sm text-ink focus:border-rh focus:outline-none focus:ring-2 focus:ring-rh';

interface Formation {
  id: string; intitule: string; organisme: string | null; lieu: string | null;
  dateDebut: string; dateFin: string | null;
  heureDebut: string | null; heureFin: string | null;
  obligatoire: boolean;
  cout: number | null;
  habilitationTypeId: string | null; habilitationTypeLibelle: string | null;
  aEmargementSession: boolean; clotureeAt: string | null;
  inscrits: number; presents: number; emargements: number;
}
interface Inscription {
  id: string; agentId: string; agentNom: string; statut: string;
  presenceAt: string | null; aEmargement: boolean; commentaire: string | null;
}

function dateFr(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

export function RhFormationsPanel() {
  const [formations, setFormations] = useState<Formation[]>([]);
  const [ouverte, setOuverte] = useState<Formation | null>(null);
  const [inscriptions, setInscriptions] = useState<Inscription[]>([]);
  const [agents, setAgents] = useState<Array<{ id: string; nom: string }>>([]);
  const [typesHab, setTypesHab] = useState<Array<{ id: string; libelle: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const { confirm, dialogElement } = useConfirmDialog();

  const [form, setForm] = useState(false);
  const [intitule, setIntitule] = useState('');
  const [organisme, setOrganisme] = useState('');
  const [lieu, setLieu] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [heureDebut, setHeureDebut] = useState('');
  const [heureFin, setHeureFin] = useState('');
  const [obligatoire, setObligatoire] = useState(false);
  const [cout, setCout] = useState('');
  const [habilitationTypeId, setHabilitationTypeId] = useState('');
  const [saving, setSaving] = useState(false);

  const [aInscrire, setAInscrire] = useState<string[]>([]);
  /** Clôture : on ne coche que les ABSENTS — le cas rare. */
  const [absentsCloture, setAbsentsCloture] = useState<string[]>([]);
  const [cloturing, setCloturing] = useState(false);

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setFormations((await api.rh.formations.list()).formations);
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Chargement impossible');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void charger(); }, [charger]);

  useEffect(() => {
    void (async () => {
      try {
        const d = await api.rh.dossiersAgents();
        setAgents(d.map((a) => ({ id: a.id, nom: `${a.prenom} ${a.nom}` })));
      } catch { /* la liste d'agents n'est utile qu'à l'inscription */ }
      try {
        const t = await api.rh.habilitations.types();
        setTypesHab(t.filter((x) => x.actif).map((x) => ({ id: x.id, libelle: x.libelle })));
      } catch { /* le rebouclage est optionnel */ }
    })();
  }, []);

  async function ouvrir(f: Formation) {
    setError(null);
    try {
      setInscriptions((await api.rh.formations.inscriptions(f.id)).inscriptions);
      setOuverte(f);
      setAInscrire([]);
      setAbsentsCloture([]);
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Ouverture impossible');
    }
  }

  /** Recharge la liste ET la fiche ouverte (compteurs, clôture, feuille). */
  async function rafraichir() {
    const liste = (await api.rh.formations.list()).formations;
    setFormations(liste);
    if (ouverte) {
      const maj = liste.find((f) => f.id === ouverte.id);
      if (maj) {
        setOuverte(maj);
        setInscriptions((await api.rh.formations.inscriptions(maj.id)).inscriptions);
      }
    }
  }

  async function creer() {
    if (!intitule.trim() || !dateDebut) return;
    setSaving(true); setError(null);
    try {
      await api.rh.formations.creer({
        intitule: intitule.trim(),
        organisme: organisme.trim() || null,
        lieu: lieu.trim() || null,
        dateDebut,
        dateFin: dateFin || null,
        heureDebut: heureDebut || null,
        heureFin: heureFin || null,
        obligatoire,
        cout: cout.trim() ? Number(cout.replace(',', '.')) : null,
        habilitationTypeId: habilitationTypeId || null,
      });
      setForm(false);
      setIntitule(''); setOrganisme(''); setLieu('');
      setDateDebut(''); setDateFin(''); setHeureDebut(''); setHeureFin('');
      setObligatoire(false); setCout(''); setHabilitationTypeId('');
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Création impossible');
    } finally { setSaving(false); }
  }

  async function deposerFeuilleSession(fichier: File) {
    if (!ouverte) return;
    setError(null);
    try {
      await api.rh.formations.deposerEmargementSession(ouverte.id, fichier);
      setMsg("Feuille d'émargement de la session déposée.");
      await rafraichir();
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Dépôt impossible');
    }
  }

  async function voirFeuilleSession() {
    if (!ouverte) return;
    try {
      const { url } = await api.rh.formations.emargementSessionUrl(ouverte.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Document indisponible');
    }
  }

  async function cloturer() {
    if (!ouverte) return;
    const nb = inscriptions.filter((i) => i.statut !== 'annule').length;
    const nbAbs = absentsCloture.length;
    const ok = await confirm({
      title: 'Clôturer la session ?',
      description:
        `${nb - nbAbs} agent${nb - nbAbs > 1 ? 's' : ''} sera${nb - nbAbs > 1 ? 'ont' : ''} `
        + `marqué${nb - nbAbs > 1 ? 's' : ''} présent${nb - nbAbs > 1 ? 's' : ''}`
        + `${nbAbs ? ` et ${nbAbs} absent${nbAbs > 1 ? 's' : ''}` : ''}.`
        + `${ouverte.habilitationTypeLibelle
          ? `\n\nLes échéances « ${ouverte.habilitationTypeLibelle} » des présents seront recalculées pour la prochaine relance.` : ''}`
        + `${ouverte.organisme || ouverte.cout !== null
          ? '\nLa comptabilité sera prévenue que la facture de l\'organisme arrive.' : ''}`,
      confirmLabel: 'Clôturer',
    });
    if (!ok) return;
    setCloturing(true); setError(null);
    try {
      const r = await api.rh.formations.cloturer(ouverte.id, absentsCloture);
      let m = `Session clôturée : ${r.presents} présent${r.presents > 1 ? 's' : ''}`
        + `${r.absents ? `, ${r.absents} absent${r.absents > 1 ? 's' : ''}` : ''}.`;
      if (r.habilitations.length) {
        m += ` ${r.habilitations.length} habilitation${r.habilitations.length > 1 ? 's' : ''} rebouclée${r.habilitations.length > 1 ? 's' : ''}.`;
      }
      if (r.nonReboucles.length) {
        m += ` À reboucler à la main (pas de compte) : ${r.nonReboucles.join(', ')}.`;
      }
      if (r.comptaPrevenue) m += ' Comptabilité prévenue.';
      setMsg(m);
      await rafraichir();
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Clôture impossible');
    } finally { setCloturing(false); }
  }

  async function inscrire() {
    if (!ouverte || aInscrire.length === 0) return;
    setError(null);
    try {
      const r = await api.rh.formations.inscrire(ouverte.id, aInscrire);
      setMsg(`${r.inscrits} agent${r.inscrits > 1 ? 's' : ''} inscrit${r.inscrits > 1 ? 's' : ''}.`);
      await ouvrir(ouverte);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Inscription impossible');
    }
  }

  async function pointer(i: Inscription, present: boolean) {
    setError(null);
    try {
      await api.rh.formations.pointer(i.id, present);
      if (ouverte) { await ouvrir(ouverte); await charger(); }
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Pointage impossible');
    }
  }

  async function deposer(i: Inscription, fichier: File) {
    setError(null);
    try {
      await api.rh.formations.deposerEmargement(i.id, fichier);
      setMsg('Émargement déposé.');
      if (ouverte) { await ouvrir(ouverte); await charger(); }
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Dépôt impossible');
    }
  }

  async function voirEmargement(i: Inscription) {
    try {
      const { url } = await api.rh.formations.emargementUrl(i.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Document indisponible');
    }
  }

  if (loading) {
    return (
      <Card variant="glass" padded>
        <p className="flex items-center gap-2 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Chargement…
        </p>
      </Card>
    );
  }

  // ── Détail d'une session ────────────────────────────────────────────────
  if (ouverte) {
    const nonInscrits = agents.filter(
      (a) => !inscriptions.some((i) => i.agentId === a.id),
    );
    return (
      <div className="space-y-3">
        {dialogElement}
        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
        {msg && <Alert variant="success" icon={Check}>{msg}</Alert>}

        <button type="button" onClick={() => { setOuverte(null); setMsg(null); }}
          className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink">
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Toutes les formations
        </button>

        <Card variant="glass" padded>
          <h2 className="text-base font-semibold text-ink">{ouverte.intitule}</h2>
          <p className="mt-0.5 text-xs text-ink-muted">
            {dateFr(ouverte.dateDebut)}
            {ouverte.dateFin && ouverte.dateFin !== ouverte.dateDebut && ` → ${dateFr(ouverte.dateFin)}`}
            {ouverte.heureDebut && ` · ${ouverte.heureDebut}${ouverte.heureFin ? `–${ouverte.heureFin}` : ''}`}
            {ouverte.organisme && ` · ${ouverte.organisme}`}
            {ouverte.lieu && ` · ${ouverte.lieu}`}
            {ouverte.cout !== null && ` · ${ouverte.cout.toFixed(2).replace('.', ',')} €`}
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {ouverte.clotureeAt && <Badge variant="success" size="sm">Clôturée</Badge>}
            {ouverte.habilitationTypeLibelle && (
              <Badge variant="info" size="sm">Reboucle : {ouverte.habilitationTypeLibelle}</Badge>
            )}
            <Badge variant="info" size="sm">{ouverte.inscrits} inscrits</Badge>
            <Badge variant={ouverte.presents === ouverte.inscrits && ouverte.inscrits > 0 ? 'success' : 'neutral'} size="sm">
              {ouverte.presents} présents
            </Badge>
            <Badge variant="neutral" size="sm">{ouverte.emargements} émargements</Badge>
          </div>
        </Card>

        {/* La feuille que rend l'ORGANISME : une par session. */}
        <Card variant="glass" padded>
          <h3 className="mb-2 flex items-center gap-1.5 text-sm font-medium text-ink">
            <FileText className="h-4 w-4" aria-hidden="true" />
            Feuille d'émargement de la session
          </h3>
          <div className="flex flex-wrap items-center gap-1.5">
            <label
              htmlFor="emarg-session"
              className="inline-flex cursor-pointer items-center gap-1 rounded-r-md border border-border px-2.5 py-1 text-xs text-ink-muted hover:border-rh/40 hover:text-ink"
            >
              <Camera className="h-3.5 w-3.5" aria-hidden="true" />
              {ouverte.aEmargementSession ? 'Remplacer la feuille' : 'Déposer la feuille de l\'organisme'}
            </label>
            <input id="emarg-session" type="file" className="sr-only"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void deposerFeuilleSession(f);
                e.target.value = '';
              }} />
            {ouverte.aEmargementSession && (
              <Button variant="ghost" size="sm" onClick={() => void voirFeuilleSession()}>
                Voir
              </Button>
            )}
          </div>
        </Card>

        {/* Clôture — tous présents d'un coup, seuls les absents se cochent. */}
        {!ouverte.clotureeAt && inscriptions.some((i) => i.statut !== 'annule') && (
          <Card variant="glass" padded>
            <h3 className="mb-1 flex items-center gap-1.5 text-sm font-medium text-ink">
              <Lock className="h-4 w-4" aria-hidden="true" /> Clôturer la session
            </h3>
            <p className="mb-2 text-xs text-ink-muted">
              Tout le monde passe présent d'un coup — cochez uniquement les
              absents, s'il y en a.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {inscriptions.filter((i) => i.statut !== 'annule').map((i) => {
                const abs = absentsCloture.includes(i.id);
                return (
                  <button key={i.id} type="button" aria-pressed={abs}
                    onClick={() => setAbsentsCloture(abs
                      ? absentsCloture.filter((x) => x !== i.id)
                      : [...absentsCloture, i.id])}
                    className={`rounded-r-md border px-2.5 py-1 text-xs transition-all ${abs ? 'border-danger/50 bg-danger/10 text-ink' : 'border-border text-ink-muted hover:border-danger/40'}`}>
                    {abs ? 'Absent : ' : ''}{i.agentNom}
                  </button>
                );
              })}
            </div>
            <Button variant="primary" size="sm" className="mt-3" loading={cloturing}
              onClick={() => void cloturer()}>
              Clôturer — {inscriptions.filter((i) => i.statut !== 'annule').length - absentsCloture.length} présent{inscriptions.filter((i) => i.statut !== 'annule').length - absentsCloture.length > 1 ? 's' : ''}
            </Button>
            {ouverte.habilitationTypeLibelle && (
              <p className="mt-2 text-2xs text-ink-muted">
                À la clôture, l'échéance « {ouverte.habilitationTypeLibelle} » des
                présents est recalculée pour la prochaine relance.
              </p>
            )}
          </Card>
        )}

        {nonInscrits.length > 0 && (
          <Card variant="glass" padded>
            <h3 className="mb-2 text-sm font-medium text-ink">Inscrire des agents</h3>
            <div className="flex flex-wrap gap-1.5">
              {nonInscrits.map((a) => {
                const actif = aInscrire.includes(a.id);
                return (
                  <button key={a.id} type="button" aria-pressed={actif}
                    onClick={() => setAInscrire(actif
                      ? aInscrire.filter((x) => x !== a.id)
                      : [...aInscrire, a.id])}
                    className={`rounded-r-md border px-2.5 py-1 text-xs transition-all ${actif ? 'border-rh/50 bg-rh/10 text-ink' : 'border-border text-ink-muted hover:border-rh/40'}`}>
                    {a.nom}
                  </button>
                );
              })}
            </div>
            <Button variant="primary" size="sm" className="mt-3"
              disabled={aInscrire.length === 0} onClick={() => void inscrire()}>
              Inscrire ({aInscrire.length})
            </Button>
            <p className="mt-2 text-2xs text-ink-muted">
              Les agents disposant d'un compte VPC reçoivent une notification.
            </p>
          </Card>
        )}

        <Card variant="glass" padded>
          <h3 className="mb-1 text-sm font-medium text-ink">Feuille de présence</h3>
          <Alert variant="info" icon={Info} className="mb-3">
            Déposer la photo de la feuille signée vaut constat de présence — pas
            besoin de cocher en plus.
          </Alert>

          {inscriptions.length === 0 ? (
            <p className="py-3 text-center text-sm text-ink-muted">Aucun inscrit.</p>
          ) : (
            <ul className="divide-y divide-border">
              {inscriptions.map((i) => (
                <li key={i.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <div className="min-w-0">
                    <p className="text-sm text-ink">{i.agentNom}</p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                      <Badge
                        variant={i.statut === 'present' ? 'success' : i.statut === 'absent' ? 'urgent' : 'neutral'}
                        size="sm"
                      >
                        {i.statut === 'present' ? 'Présent' : i.statut === 'absent' ? 'Absent' : 'Inscrit'}
                      </Badge>
                      {i.aEmargement && <Badge variant="info" size="sm">Émargement</Badge>}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5">
                    <Button variant="ghost" size="sm" onClick={() => void pointer(i, true)}>
                      <Check className="h-4 w-4" aria-hidden="true" /> Présent
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => void pointer(i, false)}>
                      <X className="h-4 w-4" aria-hidden="true" /> Absent
                    </Button>

                    <label
                      htmlFor={`emarg-${i.id}`}
                      className="inline-flex cursor-pointer items-center gap-1 rounded-r-md border border-border px-2.5 py-1 text-xs text-ink-muted hover:border-rh/40 hover:text-ink"
                    >
                      <Camera className="h-3.5 w-3.5" aria-hidden="true" />
                      {i.aEmargement ? 'Remplacer' : 'Photo'}
                    </label>
                    <input id={`emarg-${i.id}`} type="file" className="sr-only"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void deposer(i, f);
                        e.target.value = '';
                      }} />

                    {i.aEmargement && (
                      <Button variant="ghost" size="sm" onClick={() => void voirEmargement(i)}>
                        Voir
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    );
  }

  // ── Liste des sessions ──────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}

      {/* M27 (retour Manon) — les souhaits de formation déposés par les agents. */}
      <DemandesFormationRhCard />
      {/* M28 — ordres de mission à valider (souvent issus d'une formation). */}
      <DeplacementsRhCard />

      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-sm font-medium text-ink">
          <GraduationCap className="h-4 w-4" aria-hidden="true" /> Formations
        </h2>
        {!form && (
          <Button variant="primary" size="sm" onClick={() => setForm(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" /> Nouvelle session
          </Button>
        )}
      </div>

      {form && (
        <Card variant="glass" padded>
          <div className="space-y-3">
            <Field label="Intitulé *" htmlFor="fo-int">
              <input id="fo-int" value={intitule} onChange={(e) => setIntitule(e.target.value)}
                maxLength={200} placeholder="Ex : CACES R482 — recyclage" className={CHAMP} />
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Organisme" htmlFor="fo-org">
                <input id="fo-org" value={organisme} onChange={(e) => setOrganisme(e.target.value)}
                  maxLength={150} className={CHAMP} />
              </Field>
              <Field label="Lieu" htmlFor="fo-lieu">
                <input id="fo-lieu" value={lieu} onChange={(e) => setLieu(e.target.value)}
                  maxLength={200} className={CHAMP} />
              </Field>
              <Field label="Date de début *" htmlFor="fo-d1">
                <input id="fo-d1" type="date" value={dateDebut}
                  onChange={(e) => setDateDebut(e.target.value)} className={CHAMP} />
              </Field>
              <Field label="Date de fin" htmlFor="fo-d2">
                <input id="fo-d2" type="date" value={dateFin}
                  onChange={(e) => setDateFin(e.target.value)} className={CHAMP} />
              </Field>
              <Field label="Heure de début" htmlFor="fo-h1">
                <input id="fo-h1" type="time" value={heureDebut}
                  onChange={(e) => setHeureDebut(e.target.value)} className={CHAMP} />
              </Field>
              <Field label="Heure de fin" htmlFor="fo-h2">
                <input id="fo-h2" type="time" value={heureFin}
                  onChange={(e) => setHeureFin(e.target.value)} className={CHAMP} />
              </Field>
              <Field label="Coût (€)" htmlFor="fo-cout">
                <input id="fo-cout" type="text" inputMode="decimal" value={cout}
                  onChange={(e) => setCout(e.target.value)}
                  placeholder="Ex : 850" className={CHAMP} />
              </Field>
              <Field label="Habilitation rebouclée à la clôture" htmlFor="fo-hab">
                <select id="fo-hab" value={habilitationTypeId}
                  onChange={(e) => setHabilitationTypeId(e.target.value)} className={CHAMP}>
                  <option value="">— Aucune —</option>
                  {typesHab.map((t) => (
                    <option key={t.id} value={t.id}>{t.libelle}</option>
                  ))}
                </select>
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={obligatoire}
                onChange={(e) => setObligatoire(e.target.checked)}
                className="h-4 w-4 rounded border-border" />
              Formation obligatoire (plan de formation)
            </label>
            <div className="flex gap-2">
              <Button variant="primary" size="sm" loading={saving}
                disabled={!intitule.trim() || !dateDebut} onClick={() => void creer()}>
                Créer la session
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setForm(false)}>Annuler</Button>
            </div>
          </div>
        </Card>
      )}

      {formations.length === 0 ? (
        <Card variant="glass" padded>
          <p className="py-6 text-center text-sm text-ink-muted">
            Aucune session enregistrée.
          </p>
        </Card>
      ) : (
        <div className="space-y-2">
          {formations.map((f) => (
            <button key={f.id} type="button" onClick={() => void ouvrir(f)}
              className="block w-full text-left">
              <Card variant="glass" padded className="transition-colors hover:border-rh/40">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink">{f.intitule}</p>
                    <p className="mt-0.5 text-2xs text-ink-muted">
                      {dateFr(f.dateDebut)}
                      {f.organisme && ` · ${f.organisme}`}
                    </p>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1.5">
                    {f.clotureeAt && <Badge variant="success" size="sm">Clôturée</Badge>}
                    {f.obligatoire && <Badge variant="warning" size="sm">Obligatoire</Badge>}
                    <Badge variant="info" size="sm">{f.inscrits}</Badge>
                    {f.inscrits > 0 && (
                      <Badge variant={f.emargements === f.inscrits ? 'success' : 'neutral'} size="sm">
                        {f.emargements}/{f.inscrits} émargés
                      </Badge>
                    )}
                  </div>
                </div>
              </Card>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
